/**
 * Live Architecture / Functional / Data-Sovereignty docs viewer.
 *
 * Renders the auto-generated HTML from `/api/docs/architecture.html`
 * and `/api/docs/functional.html` — both regenerated on every request
 * from the running codebase + collection list.
 *
 * Why we don't iframe `src=...` anymore:
 *   The production reverse-proxy adds `X-Frame-Options: DENY`/
 *   `frame-ancestors 'none'` to every response, which blocks the
 *   browser from loading the docs HTML inside an iframe via URL
 *   (ERR_BLOCKED_BY_RESPONSE). The fix is to FETCH the HTML through
 *   the API client (cookie auth + CORS already configured) and inject
 *   it into the iframe via `srcDoc`. srcDoc content is rendered from
 *   a string, so frame-ancestors never applies. Same isolation as a
 *   src-loaded iframe (the docs CSS can't bleed into the dashboard),
 *   zero proxy-header dependency.
 *
 * Also surfaces the `/api/docs/data-sovereignty` JSON view as a
 * structured panel inline so operators can prove to auditors / partners
 * where each data type physically lives.
 */
import React, { useEffect, useState } from 'react';
import { FileText, Download, Network, Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the docs fetch effects. */

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function ArchitectureDocs() {
  const [tab, setTab] = useState('architecture'); // 'architecture' | 'functional' | 'sovereignty'
  const [sov, setSov] = useState(null);
  const [html, setHtml] = useState({ architecture: null, functional: null });
  const [loading, setLoading] = useState({ architecture: false, functional: false });
  const [error, setError] = useState({ architecture: null, functional: null });

  // Sovereignty JSON loads once on mount.
  useEffect(() => {
    api.get('/docs/data-sovereignty').then(({ data }) => setSov(data)).catch(() => {});
  }, []);

  // Architecture + Functional HTML are fetched on tab-switch (and
  // memoised so flipping back is instant). We use api.get with
  // responseType:'text' so axios doesn't try to JSON-parse the body.
  useEffect(() => {
    if (tab === 'sovereignty') return;
    if (html[tab] !== null) return;          // already loaded
    setLoading((l) => ({ ...l, [tab]: true }));
    setError((e) => ({ ...e, [tab]: null }));
    api.get(`/docs/${tab}.html`, { responseType: 'text', transformResponse: [(v) => v] })
      .then(({ data }) => setHtml((prev) => ({ ...prev, [tab]: String(data) })))
      .catch((e) => setError((prev) => ({ ...prev, [tab]: e?.message || 'Could not load doc.' })))
      .finally(() => setLoading((l) => ({ ...l, [tab]: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const tabs = [
    { id: 'architecture', label: 'Technical Architecture',  Icon: Network },
    { id: 'functional',   label: 'Functional Specification', Icon: FileText },
    { id: 'sovereignty',  label: 'Data Sovereignty Map',     Icon: Network },
  ];

  return (
    <div className="space-y-5" data-testid="docs-page">
      <div className="pal-card pal-grid-bg p-5">
        <div className="pal-bracket">[LIVE · ARCHITECTURE &amp; FUNCTIONAL DOCS]</div>
        <h1 className="display text-2xl mt-0.5">Auto-generated from the running codebase.</h1>
        <p className="text-sm opacity-70 mt-1 max-w-2xl">
          Every doc on this page is re-rendered from the actual source — route table, model classes, collection list, page count. No drifting Notion page.
        </p>
        <div className="mt-4 flex gap-2 flex-wrap">
          {tabs.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`docs-tab-${id}`}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.20em] font-bold rounded pal-mono flex items-center gap-1.5 border ${tab === id ? 'bg-[var(--ve-violet)] text-black border-[var(--ve-violet)]' : 'border-[rgba(174,82,234,0.30)] hover:bg-[rgba(174,82,234,0.06)]'}`}>
              <Icon size={11} /> {label}
            </button>
          ))}
          {tab !== 'sovereignty' && (
            <a href={`${BACKEND}/api/docs/${tab}.html`} target="_blank" rel="noreferrer" data-testid="docs-open-tab"
              className="ml-auto px-3 py-2 text-[10px] uppercase tracking-[0.20em] font-bold rounded pal-mono border border-[rgba(174,82,234,0.30)] hover:bg-[rgba(174,82,234,0.06)] flex items-center gap-1.5">
              <Download size={11} /> OPEN IN NEW TAB
            </a>
          )}
        </div>
      </div>

      {tab === 'sovereignty' && sov && (
        <div className="space-y-4" data-testid="docs-sovereignty">
          <Bucket title="STAYS INSIDE THE CLUSTER" items={sov.stays_inside_cluster} tone="ok" />
          <Bucket title="NEVER LEAVES (ZERO-KNOWLEDGE)" items={sov.never_leaves} tone="violet" />
          <Bucket title="LEAVES TO LLM PROVIDER (DEFAULT MODE)" items={sov.leaves_cluster_to_third_party_when_default_llm_is_used} tone="warn" />
          <Bucket title="STAYS INSIDE YOUR AWS (BEDROCK MODE)" items={sov.leaves_cluster_when_aws_bedrock_is_connected} tone="ok" />
        </div>
      )}

      {tab !== 'sovereignty' && (
        <div className="w-full rounded-md border border-[rgba(174,82,234,0.18)] bg-black overflow-hidden"
             style={{ height: 'calc(100vh - 280px)', minHeight: '600px' }}>
          {loading[tab] && (
            <div className="h-full flex items-center justify-center text-xs uppercase tracking-[0.2em] opacity-70 gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          )}
          {error[tab] && !loading[tab] && (
            <div className="h-full flex flex-col items-center justify-center text-center px-8 gap-2">
              <AlertTriangle size={22} className="text-rose-400" />
              <div className="text-xs uppercase tracking-[0.2em] opacity-80">Could not load this doc.</div>
              <div className="text-[11px] opacity-60 max-w-md">{error[tab]}</div>
              <a href={`${BACKEND}/api/docs/${tab}.html`} target="_blank" rel="noreferrer"
                 className="mt-3 text-[10px] uppercase tracking-[0.2em] underline opacity-80">
                Open in new tab instead
              </a>
            </div>
          )}
          {html[tab] && !loading[tab] && !error[tab] && (
            <iframe
              // srcDoc renders the HTML from a string — the production
              // reverse-proxy's X-Frame-Options / frame-ancestors header
              // cannot block this because the iframe never makes a
              // cross-document load.
              srcDoc={html[tab]}
              title={`docs-${tab}`}
              data-testid={`docs-iframe-${tab}`}
              sandbox="allow-same-origin allow-popups"
              className="w-full h-full border-0"
            />
          )}
        </div>
      )}
    </div>
  );
}

function Bucket({ title, items, tone }) {
  return (
    <div className="pal-card pal-grid-bg p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="pal-bracket">[{title}]</span>
        <span className={`pal-status pal-status-${tone}`}>{items.length}</span>
      </div>
      <ul className="space-y-1 pl-1">
        {items.map((i) => (
          <li key={i} className="pal-mono text-[11px] opacity-85 flex gap-2 items-start">
            <span className="opacity-50 mt-0.5">·</span>
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
